import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReceiptApproval() {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch both pending and rejected receipts
      const response = await axios.get(`${API}/receipts/pending-and-rejected`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    }
  };

  const handleApprove = async (receiptId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/receipts/${receiptId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Approved! ${response.data.coupons_generated} points generated.`);
      fetchReceipts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve receipt');
    }
  };

  const handleReject = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/receipts/${selectedReceipt.receipt_id}/reject?reason=${encodeURIComponent(rejectionReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Receipt rejected');
      setShowRejectDialog(false);
      setRejectionReason('');
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to reject receipt');
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12" data-testid="receipt-approval-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/business/dashboard')}
          variant="ghost"
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <h1 className="font-playfair font-bold text-4xl text-primary mb-8">Receipt Approval Queue</h1>

        {(receipts || []).length === 0 ? (
          <Card className="p-12 text-center">
            <p className="font-outfit text-gray-600">No pending receipts</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {(receipts || []).map((receipt) => (
              <Card key={receipt.receipt_id} className="p-6" data-testid={`receipt-${receipt.receipt_id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-playfair font-bold text-2xl text-primary">${receipt.transaction_amount}</h3>
                      {receipt.status === 'rejected' && (
                        <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                          REJECTED
                        </span>
                      )}
                      {receipt.status === 'pending' && (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                          PENDING
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm font-outfit text-gray-600">
                      <p><strong>Receipt Number:</strong> {receipt.receipt_number || 'N/A'}</p>
                      <p><strong>Customer:</strong> {receipt.customer_name}</p>
                      <p><strong>Event:</strong> {receipt.event_name || 'N/A'}</p>
                      <p><strong>Business:</strong> {receipt.business_name}</p>
                      <p><strong>Date:</strong> {new Date(receipt.transaction_date).toLocaleDateString()}</p>
                      <p><strong>Submitted:</strong> {new Date(receipt.submitted_at).toLocaleString()}</p>
                      {receipt.person_helped && (
                        <p><strong>Person Helped:</strong> {receipt.person_helped}</p>
                      )}
                      {receipt.status === 'rejected' && receipt.rejection_reason && (
                        <p className="text-red-600"><strong>Rejection Reason:</strong> {receipt.rejection_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(receipt.receipt_id)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-${receipt.receipt_id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    {receipt.status !== 'rejected' && (
                      <Button
                        onClick={() => {
                          setSelectedReceipt(receipt);
                          setShowRejectDialog(true);
                        }}
                        variant="destructive"
                        data-testid={`reject-${receipt.receipt_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Reject Receipt</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Rejection Reason</Label>
                <Input
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection"
                  required
                  data-testid="rejection-reason-input"
                />
              </div>
              <Button
                onClick={handleReject}
                className="w-full"
                disabled={!rejectionReason}
                data-testid="submit-reject-button"
              >
                Confirm Rejection
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}